import { useId, useMemo, useRef, useState } from "react";

/** 파일 입력(드래그앤드롭 + 버튼 + 파일명 표시 + 이미지 미리보기 + 지우기) */
export default function FileInput({
    accept,
    required = false,
    onFileChange,
    value,              // File | File[] | null
    placeholder = "",
    showPreview = false,
    multiple = false,
}) {
    const id = useId();
    const inputRef = useRef(null);
    const dragCounter = useRef(0);
    const [isDragging, setDragging] = useState(false);

    const hasFile = useMemo(() => {
        return multiple ? Array.isArray(value) && value.length > 0 : !!value;
    }, [value, multiple]);

    const name = useMemo(() => {
        if (!hasFile) return "";
        if (multiple && Array.isArray(value)) return value.map((f) => f.name).join(", ");
        return value.name;
    }, [hasFile, value, multiple]);

    const previewURL = useMemo(() => {
        if (!showPreview || !hasFile) return null;
        const f = multiple && Array.isArray(value) ? value[0] : value;
        if (!f || !f.type?.startsWith?.("image/")) return null;
        return URL.createObjectURL(f);
    }, [showPreview, hasFile, value, multiple]);

    const handleFiles = (files) => {
        if (!files || files.length === 0) return;
        onFileChange(multiple ? Array.from(files) : files[0] ?? null);
    };

    const onDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current += 1;
        setDragging(true);
    };
    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // 드랍 가능 커서
        e.dataTransfer.dropEffect = "copy";
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current <= 0) {
            setDragging(false);
            dragCounter.current = 0;
        }
    };
    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        dragCounter.current = 0;
        const files = e.dataTransfer?.files || [];
        handleFiles(files);
    };

    return (
        <div className="file-input">
            <div
                className={`file-drop ${isDragging ? "is-drag" : ""}`}
                onDragEnter={onDragEnter}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
            >
                <input
                    id={id}
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    style={{ display: "none" }}
                    onChange={(e) => handleFiles(e.target.files)}
                    // required={required && !hasFile}
                />

                <div className="file-row">
                    <input type="text" readOnly placeholder={placeholder} value={name} />
                    <div className="file-actions">
                        <button type="button" className="btn btn--file" onClick={() => inputRef.current?.click()}>
                            파일선택
                        </button>
                        {hasFile && (
                            <button
                                type="button"
                                className="btn btn--ghost"
                                onClick={() => {
                                    onFileChange(multiple ? [] : null);
                                    if (inputRef.current) inputRef.current.value = "";
                                }}
                                title="지우기"
                            >
                                지우기
                            </button>
                        )}
                    </div>
                </div>

                <div className="file-hint">여기로 파일을 끌어다 놓거나, <b>파일선택</b> 버튼을 클릭하세요.</div>
                {previewURL && (
                    <div className="file-preview">
                        <img
                            src={previewURL}
                            alt="미리보기"
                            onLoad={() => URL.revokeObjectURL(previewURL)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
